/**
 * Leif-i18n (de/fr/en) im Gettext-Stil: der deutsche Text IST der Key.
 *
 * - `t("Anmelden")` liefert für de den Key selbst, für en/fr den Eintrag aus
 *   dem Wörterbuch ([en, fr]-Tupel in `i18n.dict.ts`). Fehlt ein Eintrag,
 *   fällt es lautlos auf Deutsch zurück — nichts bricht, Lücken bleiben
 *   sichtbar und messbar (scripts/check-i18n.mjs).
 * - Interpolation über `{name}`-Platzhalter: t("Zeile {n}", { n: 42 }).
 * - Sprache: localStorage-Override (`leif:lang`) → sonst Browser-Sprache
 *   (de/fr erkannt, alles andere → en). Ein Wechsel lädt die Seite neu —
 *   dadurch braucht es keinen React-Context und `t()` bleibt eine reine
 *   Funktion, die überall (auch im Canvas-Code) nutzbar ist.
 */
import { DICT } from "./i18n.dict";

export type Lang = "de" | "en" | "fr";

const STORAGE_KEY = "leif:lang";

function detect(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "de" || stored === "en" || stored === "fr") return stored;
  } catch {
    /* Storage gesperrt → Auto */
  }
  const raw = (typeof navigator !== "undefined" ? navigator.language : "de").toLowerCase();
  if (raw.startsWith("de")) return "de";
  if (raw.startsWith("fr")) return "fr";
  return "en";
}

/** Aktive Sprache — einmal beim Laden bestimmt (Wechsel = Reload). */
export const lang: Lang = detect();

/** Sprache umschalten und App neu laden (einfach + garantiert konsistent). */
export function setLang(next: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore */
  }
  window.location.reload();
}

/** Übersetzen + `{name}`-Platzhalter füllen. Fallback = deutscher Key. */
export function t(de: string, params?: Record<string, string | number>): string {
  let s = de;
  if (lang !== "de") {
    const entry = DICT[de];
    if (entry) s = lang === "en" ? entry[0] : entry[1];
  }
  if (params) {
    s = s.replace(/\{(\w+)\}/g, (m, k) => (k in params ? String(params[k]) : m));
  }
  return s;
}

/** BCP-47-Locale für Datum/Zeit-Formatierung (`toLocaleString(dateLocale())`). */
export function dateLocale(): string {
  return lang === "de" ? "de-DE" : lang === "fr" ? "fr-FR" : "en-GB";
}

/** Dezimaltrennzeichen für handformatierte Zahlen (Zeitstrahl-Instrument). */
export function decimalSep(): string {
  return lang === "en" ? "." : ",";
}
